import LanguageDetect from "languagedetect";
import {LANGUAGES, supportedLanguages} from "config";

const lngDetector = new LanguageDetect();

// The detector's cost grows with its input, and these run on every render of a
// document of up to 500k chars: a sample tells the language as well as the whole.
const ALIGNMENT_SAMPLE_CHARS = 1000;
const ALIGNMENT_SAMPLES = 3;
const SUPPORT_CHUNK_CHARS = 1000;
const SUPPORT_MAX_CHUNKS = 64;

// `languagedetect` reports names ("english", "german"), the app speaks codes.
// Slicing the name to two letters used to be close enough for en/ar, but it
// turns german into "ge" and spanish into "sp" — so map through the registry.
const CODE_BY_DETECTOR_NAME: Record<string, string> =
    Object.fromEntries(LANGUAGES.map((l) => [l.detectorName, l.code]));

/** Most probable language of the text, as one of our 2-letter codes. */
export const getTextLanguage = (text: string): string | undefined => {
    const detector = new LanguageDetect();
    const languages = detector.detect(text, 3).map(e => e[0]);
    if (languages.length > 0) {
        return CODE_BY_DETECTOR_NAME[languages[0]];
    }
    return undefined;
}

export const getTextAlignment = (text: string): { textAlign?: 'left' | 'right'; direction?: 'ltr' | 'rtl' } => {
    // Returns text alignment style based on detected language
    // Arabic text should be right-aligned, English and others left-aligned
    if (!text || text.trim().length === 0) {
        return {};
    }
    // Start, middle and end vote, so an opening in another script (a title page, an
    // abstract) doesn't decide the direction of the whole document. Each is detected
    // on its own: the detector judges a mixed sample by its first script.
    const step = Math.max(0, text.length - ALIGNMENT_SAMPLE_CHARS) / (ALIGNMENT_SAMPLES - 1);
    const votes = text.length <= ALIGNMENT_SAMPLE_CHARS * ALIGNMENT_SAMPLES ? [getTextLanguage(text)]
        : Array.from({length: ALIGNMENT_SAMPLES}, (_, i) => getTextLanguage(
            text.slice(Math.round(i * step), Math.round(i * step) + ALIGNMENT_SAMPLE_CHARS)));
    const rtl = votes.filter(v => v === 'ar').length * 2 > votes.length;
    return {
        textAlign: rtl ? 'right' : 'left',
        direction: rtl ? 'rtl' : 'ltr'
    };
}

export function checkIsSupportedLanguage(str: string) {
    if (!str || str.length === 0) return false;

    // A long text is judged on chunks spread evenly across it.
    const chunks = Math.ceil(str.length / SUPPORT_CHUNK_CHARS);
    const step = Math.max(1, chunks / SUPPORT_MAX_CHUNKS);
    let sampledChars = 0;
    let supportedChars = 0;

    for (let c = 0; c < chunks; c += step) {
        const start = Math.floor(c) * SUPPORT_CHUNK_CHARS;
        const chunk = str.slice(start, start + SUPPORT_CHUNK_CHARS);
        const languages = lngDetector.detect(chunk, 3).map((e) => e[0]);
        const isSupported = languages.some((language) =>
            supportedLanguages.includes(language)
        );
        sampledChars += chunk.length;
        if (isSupported) supportedChars += chunk.length;
    }

    return supportedChars / sampledChars >= 0.8;
}
